import { defaultRegion } from "common/config";
import { atom, useAtom, type PrimitiveAtom } from "jotai";

type CustomCoordinates = {
  latitude: number;
  longitude: number;
  accuracy?: number;
  altitude?: number | null;
  altitudeAccuracy?: number | null;
  heading?: number | null;
  speed?: number | null;
};

// Result type for requestUserLocation Promise
export type LocationResult = {
  success: boolean;
  location: CustomCoordinates;
  isUsingFallback: boolean;
};

/**
 * Shared, in-memory, for the life of the tab.
 *
 * This hook is mounted by a dozen surfaces at once, and the position belongs to
 * the person, not to whichever component asked. Kept per-instance, a grant on
 * the home page would have reached only the home page: every venue card, map
 * and "what's nearby" list on other routes would still be measuring from the
 * London default, because nothing requests automatically any more.
 *
 * Not persisted. A position is only as good as the moment it was taken, and
 * storing one would outlive the consent that produced it.
 */
const DEFAULT_LOCATION: CustomCoordinates = {
  latitude: defaultRegion.latitude,
  longitude: defaultRegion.longitude,
  accuracy: 0,
  altitude: null,
  altitudeAccuracy: null,
  heading: null,
  speed: null,
};

const locationAtom = atom<CustomCoordinates>(DEFAULT_LOCATION);
/*
 * The cast is load-bearing, exactly as it is for `loginLockoutAtom` in
 * `@/store/atoms`: with `strict: false`, `null` is assignable to a function
 * type, so `atom(null)` picks jotai's read-only overload and every setter types
 * as `never`. Drop it once strictNullChecks is on.
 */
const locationErrorAtom = atom<string | null>(null) as PrimitiveAtom<
  string | null
>;
/**
 * Only ever true while an explicit request is in flight. Nothing is requested
 * on mount, so there is nothing to wait for until the user asks.
 */
const locationLoadingAtom = atom(false);
/**
 * True until the user's real position is known - which, since nothing is
 * requested automatically, is the case from the first render. `location` holds
 * `defaultRegion` (London) and that is exactly what a fallback is.
 *
 * Callers label the location from this flag (`isUsingFallback ?
 * "Oxford Circus, London" : "Current location"`), so starting it `false` would
 * have the home page claim to be showing the user's position while it shows the
 * default.
 */
const locationIsFallbackAtom = atom(true);

export function useGeoLocation() {
  const [location, setLocation] = useAtom(locationAtom);
  const [error, setError] = useAtom(locationErrorAtom);
  const [loading, setLoading] = useAtom(locationLoadingAtom);
  const [isUsingFallback, setIsUsingFallback] = useAtom(locationIsFallbackAtom);

  /*
   * There is deliberately **no** automatic `getCurrentPosition` here.
   *
   * This hook is mounted by a dozen surfaces - home, search, every venue page,
   * the nearby maps - so an automatic request meant the browser's location
   * prompt appeared unbidden on whichever of them the user happened to land on,
   * before the app had explained itself or they had asked for anything that
   * needs a position.
   *
   * GPS is now raised only by `requestUserLocation` below, which is called from
   * the "Use current location" controls and nothing else. Until then `location`
   * serves `defaultRegion` and `isUsingFallback` says so, which is what the
   * home header reads to label it honestly as the default.
   *
   * An earlier version of this held the automatic request back only while the
   * onboarding modal was open. That is now redundant - and this version needs
   * no knowledge of onboarding at all.
   */

  const requestUserLocation = (): Promise<LocationResult> => {
    return new Promise((resolve) => {
      setLoading(true);
      setError(null);

      if (!navigator.geolocation) {
        setLoading(false);
        setError("Geolocation is not supported.");
        setIsUsingFallback(true);
        const fallbackLocation: CustomCoordinates = {
          latitude: defaultRegion.latitude,
          longitude: defaultRegion.longitude,
          accuracy: 0,
          altitude: null,
          altitudeAccuracy: null,
          heading: null,
          speed: null,
        };
        setLocation(fallbackLocation);
        resolve({
          success: false,
          location: fallbackLocation,
          isUsingFallback: true,
        });
        return;
      }

      navigator.geolocation.getCurrentPosition(
        (position) => {
          setLoading(false);
          const coords: CustomCoordinates = {
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
            accuracy: position.coords.accuracy,
            altitude: position.coords.altitude,
            altitudeAccuracy: position.coords.altitudeAccuracy,
            heading: position.coords.heading,
            speed: position.coords.speed,
          };
          setLocation(coords);
          setIsUsingFallback(false);
          resolve({
            success: true,
            location: coords,
            isUsingFallback: false,
          });
        },
        (err) => {
          setLoading(false);
          setError(err.message);
          setIsUsingFallback(true);
          const fallbackLocation: CustomCoordinates = {
            latitude: defaultRegion.latitude,
            longitude: defaultRegion.longitude,
            accuracy: 0,
            altitude: null,
            altitudeAccuracy: null,
            heading: null,
            speed: null,
          };
          setLocation(fallbackLocation);
          resolve({
            success: false,
            location: fallbackLocation,
            isUsingFallback: true,
          });
        },
        {
          enableHighAccuracy: true,
          timeout: 15000,
          maximumAge: 0,
        },
      );
    });
  };
  return { location, error, loading, requestUserLocation, isUsingFallback };
}
